import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle,
  Cancel,
  Public as PublicIcon,
  Storage as StorageIcon,
  SmartToy,
} from '@mui/icons-material';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const COLORS = ['#4F46E5', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#0ea5e9', '#a855f7'];

const FEATURE_LABELS = {
  inventory: 'Inventory',
  expenses: 'Expenses',
  parent_queries: 'Parent Queries',
  care_notes: 'Care Notes',
  daily_updates: 'Daily Updates',
  student_documents: 'Student Docs',
  academics: 'Academics',
  announcements: 'Announcements',
  leave_management: 'Leave Mgmt',
  chatbot: 'AI Chatbot',
  analytics: 'Analytics',
};

const PLATFORM_TABS = ['Overview', 'Growth', 'Finance', 'Engagement'];
const SCHOOL_TABS = ['Overview', 'Finance', 'Attendance', 'Usage'];

const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / Math.pow(1024, exponent)).toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const rateColor = (pct) => (pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444');

function getLast6Months() {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return {
      key: d.toISOString().slice(0, 7),
      label: d.toLocaleDateString('en-IN', { month: 'short' }),
    };
  });
}

const KpiCard = ({ label, value, icon, color }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ p: '16px !important' }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Box sx={{ bgcolor: `${color}15`, color, borderRadius: 1.5, p: 0.75, display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, height: '100%' }}>
    <Typography variant="subtitle1" fontWeight={600} mb={2}>{title}</Typography>
    {children}
  </Card>
);

const EmptyState = ({ message, height = 200 }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height={height}>
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);

// =====================================================================
// Platform-wide tabs
// =====================================================================

const PlatformOverview = ({ data, allSchools, navigate }) => {
  const totalSchools = allSchools.length;
  const activeSchools = allSchools.filter((s) => s.status === 'Active').length;
  const inactiveSchools = totalSchools - activeSchools;
  const totalStudents = Number(data.totals?.total_students) || 0;
  const totalTeachers = Number(data.totals?.total_teachers) || 0;
  const totalUsers = (data.userCounts || []).reduce((sum, r) => sum + (Number(r.count) || 0), 0);

  const months = getLast6Months();
  const growthData = months.map((m) => ({
    month: m.label,
    schools: data.schoolGrowth.find((r) => r.month === m.key)?.count
      ? Number(data.schoolGrowth.find((r) => r.month === m.key).count)
      : 0,
  }));

  const statusData = [
    { name: 'Active', value: activeSchools, color: '#10b981' },
    { name: 'Inactive', value: inactiveSchools, color: '#9ca3af' },
  ].filter((d) => d.value > 0);

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Total Schools" value={totalSchools} icon={<SchoolIcon sx={{ fontSize: 20 }} />} color="#4F46E5" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Active" value={activeSchools} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#10b981" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Inactive" value={inactiveSchools} icon={<Cancel sx={{ fontSize: 20 }} />} color="#ef4444" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Total Students" value={totalStudents.toLocaleString('en-IN')} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#f59e0b" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Total Teachers" value={totalTeachers.toLocaleString('en-IN')} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#6366f1" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Total Users" value={totalUsers.toLocaleString('en-IN')} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#0ea5e9" />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={7}>
          <ChartCard title="School growth (last 6 months)">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Line type="monotone" dataKey="schools" stroke="#4F46E5" strokeWidth={2.5} dot={{ r: 4, fill: '#4F46E5' }} name="New schools" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title="School status">
            {statusData.length === 0 ? (
              <EmptyState message="No schools yet" height={180} />
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <ResponsiveContainer width="55%" height={180}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={statusData.length > 1 ? 3 : 0}>
                      {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box flex={1}>
                  {statusData.map(({ name, value, color }) => (
                    <Box key={name} display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" flex={1}>{name}</Typography>
                      <Typography variant="body2" fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box p={2.5} pb={1}>
          <Typography variant="subtitle1" fontWeight={600}>Top 10 schools by student count</Typography>
        </Box>
        {data.topSchools.length === 0 ? (
          <EmptyState message="No schools yet" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>School</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Students</TableCell>
                <TableCell align="right">Teachers</TableCell>
                <TableCell align="right">Classes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.topSchools.map((s) => (
                <TableRow key={s.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/superadmin/schools/${s.id}`)}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.city || '—'}</TableCell>
                  <TableCell>{s.status}</TableCell>
                  <TableCell align="right">{Number(s.student_count).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right">{Number(s.teacher_count).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right">{Number(s.class_count).toLocaleString('en-IN')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
};

const PlatformGrowth = ({ data }) => {
  const months = getLast6Months();
  const growthData = months.map((m) => ({
    month: m.label,
    schools: data.schoolGrowth.find((r) => r.month === m.key)?.count
      ? Number(data.schoolGrowth.find((r) => r.month === m.key).count)
      : 0,
  }));

  const adoptionData = (data.featureAdoption || []).map((f) => {
    const total = Number(f.total_schools) || 0;
    const enabled = total - (Number(f.disabled_count) || 0);
    return {
      name: FEATURE_LABELS[f.feature_key] ?? f.feature_key,
      pct: total > 0 ? Math.round((enabled / total) * 100) : 0,
    };
  });

  const roleData = (data.userCounts || []).map((r, i) => ({
    name: r.role,
    value: Number(r.count) || 0,
    color: COLORS[i % COLORS.length],
  })).filter((d) => d.value > 0);

  const studentsPerSchool = (data.topSchools || []).map((s) => ({
    name: s.name,
    students: Number(s.student_count) || 0,
  }));

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <ChartCard title="New schools per month">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Bar dataKey="schools" fill="#4F46E5" name="New schools" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Feature adoption (% of schools enabled)">
            {adoptionData.length === 0 ? (
              <EmptyState message="No feature flag data yet" height={240} />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(240, adoptionData.length * 36)}>
                <BarChart data={adoptionData} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <RechartsTooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="pct" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Active users by role">
            {roleData.length === 0 ? (
              <EmptyState message="No user data yet" height={220} />
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={roleData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={roleData.length > 1 ? 3 : 0}>
                      {roleData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box flex={1}>
                  {roleData.map(({ name, value, color }) => (
                    <Box key={name} display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" flex={1} sx={{ textTransform: 'capitalize' }}>{name.replace('_', ' ')}</Typography>
                      <Typography variant="body2" fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Students per school (top 10)">
            {studentsPerSchool.length === 0 ? (
              <EmptyState message="No schools yet" height={220} />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={studentsPerSchool}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="students" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

const PlatformFinance = ({ data }) => {
  const feeHealth = data.feeHealth || {};
  const totalFees = Number(feeHealth.total_fees) || 0;
  const totalCollected = Number(feeHealth.total_collected) || 0;
  const totalPending = Number(feeHealth.total_pending) || 0;
  const collectionRate = totalFees > 0 ? Math.round((totalCollected / totalFees) * 100) : 0;

  const breakdown = data.schoolFeeBreakdown || [];
  const topByFees = breakdown.slice(0, 10).map((s) => {
    const fees = Number(s.total_fees) || 0;
    const collected = Number(s.total_collected) || 0;
    return { name: s.school_name, collected, pending: Math.max(fees - collected, 0) };
  });

  const buckets = { high: 0, mid: 0, low: 0 };
  breakdown.forEach((s) => {
    const fees = Number(s.total_fees) || 0;
    const collected = Number(s.total_collected) || 0;
    const pct = fees > 0 ? (collected / fees) * 100 : 0;
    if (pct >= 90) buckets.high++;
    else if (pct >= 50) buckets.mid++;
    else buckets.low++;
  });
  const distributionData = [
    { name: '>90% collected', value: buckets.high, color: '#10b981' },
    { name: '50-90% collected', value: buckets.mid, color: '#f59e0b' },
    { name: '<50% collected', value: buckets.low, color: '#ef4444' },
  ].filter((d) => d.value > 0);

  const months = getLast6Months();
  const trendData = months.map((m) => ({
    month: m.label,
    amount: data.paymentTrend.find((r) => r.month === m.key)?.amount
      ? Number(data.paymentTrend.find((r) => r.month === m.key).amount)
      : 0,
  }));

  const expenses = data.expenses || {};

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Total Fees Assigned" value={inr(totalFees)} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#4F46E5" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Total Collected" value={inr(totalCollected)} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#10b981" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Total Pending" value={inr(totalPending)} icon={<Cancel sx={{ fontSize: 20 }} />} color="#ef4444" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Collection Rate" value={`${collectionRate}%`} icon={<PublicIcon sx={{ fontSize: 20 }} />} color={rateColor(collectionRate)} />
        </Grid>
      </Grid>

      <Box mb={3}>
        <ChartCard title="Fee collection by school (top 10 by fees assigned)">
          {topByFees.length === 0 ? (
            <EmptyState message="No fee data recorded yet" height={200} />
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(240, topByFees.length * 36)}>
              <BarChart data={topByFees} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12 }} />
                <RechartsTooltip formatter={(v) => inr(v)} />
                <Legend />
                <Bar dataKey="collected" name="Collected" stackId="fees" fill="#10b981" />
                <Bar dataKey="pending" name="Pending" stackId="fees" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Payment collection trend (last 6 months)">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => inr(v)} width={70} />
                <RechartsTooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} name="Collected" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Collection rate distribution">
            {distributionData.length === 0 ? (
              <EmptyState message="No fee data recorded yet" height={220} />
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={distributionData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={distributionData.length > 1 ? 3 : 0}>
                      {distributionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box flex={1}>
                  {distributionData.map(({ name, value, color }) => (
                    <Box key={name} display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" flex={1}>{name}</Typography>
                      <Typography variant="body2" fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Expense claims summary</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={2.4}><KpiCard label="Total Claims" value={Number(expenses.total_claims) || 0} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#4F46E5" /></Grid>
          <Grid item xs={6} sm={2.4}><KpiCard label="Pending" value={Number(expenses.pending) || 0} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#f59e0b" /></Grid>
          <Grid item xs={6} sm={2.4}><KpiCard label="Approved" value={Number(expenses.approved) || 0} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#0ea5e9" /></Grid>
          <Grid item xs={6} sm={2.4}><KpiCard label="Paid" value={Number(expenses.paid) || 0} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#10b981" /></Grid>
          <Grid item xs={6} sm={2.4}><KpiCard label="Total Reimbursed" value={inr(expenses.total_paid_amount)} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#6366f1" /></Grid>
        </Grid>
      </Card>
    </Box>
  );
};

const PlatformEngagement = ({ data }) => {
  const chatbot = data.chatbot || {};
  const hasChatbotUsage = (Number(chatbot.total_queries) || 0) > 0;

  const storageChartData = (data.storage || []).map((s) => ({
    name: s.schoolName,
    dbMB: Number((s.dbBytes / (1024 * 1024)).toFixed(2)),
    fileMB: Number((s.fileBytes / (1024 * 1024)).toFixed(2)),
    totalBytes: s.totalBytes,
  }));

  return (
    <Box>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <SmartToy sx={{ color: '#4F46E5' }} />
          <Typography variant="subtitle1" fontWeight={600}>AI Chatbot usage (last 30 days)</Typography>
        </Box>
        {!hasChatbotUsage ? (
          <EmptyState message="No chatbot usage recorded yet. Enable the chatbot for a school to see usage data here." height={100} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <KpiCard label="Queries (30 days)" value={Number(chatbot.total_queries).toLocaleString('en-IN')} icon={<SmartToy sx={{ fontSize: 20 }} />} color="#4F46E5" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard label="Tokens used" value={Number(chatbot.total_tokens).toLocaleString('en-IN')} icon={<SmartToy sx={{ fontSize: 20 }} />} color="#6366f1" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard label="Schools using chatbot" value={Number(chatbot.schools_using_chatbot)} icon={<SmartToy sx={{ fontSize: 20 }} />} color="#0ea5e9" />
            </Grid>
          </Grid>
        )}
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <StorageIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="subtitle1" fontWeight={600}>Storage by school (DB vs Files)</Typography>
        </Box>
        {storageChartData.length === 0 ? (
          <EmptyState message="No storage data available" />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(300, storageChartData.length * 50)}>
            <BarChart data={storageChartData} layout="vertical" margin={{ left: 24 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" unit=" MB" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
              <RechartsTooltip formatter={(v, name) => [`${v} MB`, name]} />
              <Legend />
              <Bar dataKey="dbMB" name="Database" fill="#4facfe" stackId="storage" />
              <Bar dataKey="fileMB" name="Files" fill="#fa709a" stackId="storage" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </Box>
  );
};

// =====================================================================
// School-specific tabs
// =====================================================================

const SchoolOverview = ({ data }) => {
  const counts = data.counts || {};
  const today = data.attendanceToday || {};
  const total = Number(today.total) || 0;
  const present = Number(today.present) || 0;
  const attendancePct = total > 0 ? Math.round((present / total) * 100) : 0;

  const trendData = (data.attendanceTrend || []).map((r) => {
    const t = Number(r.total) || 0;
    const p = Number(r.present) || 0;
    return {
      date: new Date(r.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
      pct: t > 0 ? Math.round((p / t) * 100) : 0,
    };
  });

  const absent = Math.max(total - present, 0);
  const todayPie = [
    { name: 'Present', value: present, color: '#10b981' },
    { name: 'Absent / Other', value: absent, color: '#ef4444' },
  ].filter((d) => d.value > 0);

  const school = data.school || {};

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Students" value={Number(counts.students) || 0} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#4F46E5" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Teachers" value={Number(counts.teachers) || 0} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#6366f1" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Classes" value={Number(counts.classes) || 0} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#0ea5e9" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Attendance Today" value={`${attendancePct}%`} icon={<CheckCircle sx={{ fontSize: 20 }} />} color={rateColor(attendancePct)} />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={7}>
          <ChartCard title="30-day attendance trend">
            {trendData.length === 0 ? (
              <EmptyState message="No attendance recorded in the last 30 days" height={200} />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
                  <RechartsTooltip formatter={(v) => `${v}%`} />
                  <Line type="monotone" dataKey="pct" stroke="#4F46E5" strokeWidth={2.5} dot={false} name="Attendance %" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title="Today's attendance">
            {todayPie.length === 0 ? (
              <EmptyState message="No attendance marked today" height={180} />
            ) : (
              <Box display="flex" alignItems="center" gap={2}>
                <ResponsiveContainer width="55%" height={180}>
                  <PieChart>
                    <Pie data={todayPie} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={todayPie.length > 1 ? 3 : 0}>
                      {todayPie.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box flex={1}>
                  {todayPie.map(({ name, value, color }) => (
                    <Box key={name} display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" flex={1}>{name}</Typography>
                      <Typography variant="body2" fontWeight={600}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>School summary</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">City</Typography><Typography variant="body2" fontWeight={500}>{school.city || '—'}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Status</Typography><Typography variant="body2" fontWeight={500}>{school.status || '—'}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Contact Person</Typography><Typography variant="body2" fontWeight={500}>{school.contact_person || '—'}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Contact Email</Typography><Typography variant="body2" fontWeight={500}>{school.contact_email || '—'}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Contact Phone</Typography><Typography variant="body2" fontWeight={500}>{school.contact_phone || '—'}</Typography></Grid>
          <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Created</Typography><Typography variant="body2" fontWeight={500}>{school.created_at ? new Date(school.created_at).toLocaleDateString('en-IN') : '—'}</Typography></Grid>
        </Grid>
      </Card>
    </Box>
  );
};

const SchoolFinance = ({ data }) => {
  const feeHealth = data.feeHealth || {};
  const totalFees = Number(feeHealth.total_fees) || 0;
  const totalCollected = Number(feeHealth.total_collected) || 0;
  const totalPending = Number(feeHealth.total_pending) || 0;
  const collectionRate = totalFees > 0 ? Math.round((totalCollected / totalFees) * 100) : 0;

  const months = getLast6Months();
  const trendData = months.map((m) => ({
    month: m.label,
    amount: data.paymentTrend.find((r) => r.month === m.key)?.amount
      ? Number(data.paymentTrend.find((r) => r.month === m.key).amount)
      : 0,
  }));

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Total Fees" value={inr(totalFees)} icon={<PublicIcon sx={{ fontSize: 20 }} />} color="#4F46E5" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Collected" value={inr(totalCollected)} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#10b981" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiCard label="Pending" value={inr(totalPending)} icon={<Cancel sx={{ fontSize: 20 }} />} color="#ef4444" />
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">Collection rate</Typography>
          <Typography variant="body2" fontWeight={600}>{collectionRate}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={collectionRate}
          sx={{ height: 10, borderRadius: 5, '& .MuiLinearProgress-bar': { bgcolor: rateColor(collectionRate) } }}
        />
      </Card>

      <ChartCard title="Payment collection trend (last 6 months)">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => inr(v)} width={70} />
            <RechartsTooltip formatter={(v) => inr(v)} />
            <Line type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} name="Collected" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </Box>
  );
};

const SchoolAttendance = ({ data }) => {
  const trend = (data.attendanceTrend || []).map((r) => {
    const t = Number(r.total) || 0;
    const p = Number(r.present) || 0;
    return {
      date: r.date,
      present: p,
      absent: Math.max(t - p, 0),
      total: t,
      pct: t > 0 ? Math.round((p / t) * 100) : 0,
    };
  });
  const chartData = trend.map((r) => ({
    date: new Date(r.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
    pct: r.pct,
  }));
  const tableRows = [...trend].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Box>
      <ChartCard title="30-day attendance trend">
        {chartData.length === 0 ? (
          <EmptyState message="No attendance recorded in the last 30 days" height={220} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
              <RechartsTooltip formatter={(v) => `${v}%`} />
              <Line type="monotone" dataKey="pct" stroke="#4F46E5" strokeWidth={2.5} dot={{ r: 3 }} name="Attendance %" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 3 }}>
        <Box p={2.5} pb={1}>
          <Typography variant="subtitle1" fontWeight={600}>Recent attendance records</Typography>
        </Box>
        {tableRows.length === 0 ? (
          <EmptyState message="No records yet" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Present</TableCell>
                <TableCell align="right">Absent</TableCell>
                <TableCell align="right">Attendance %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((r) => (
                <TableRow key={r.date} hover>
                  <TableCell>{new Date(r.date).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell align="right">{r.present}</TableCell>
                  <TableCell align="right">{r.absent}</TableCell>
                  <TableCell align="right">{r.pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
};

const SchoolUsage = ({ data, navigate, featureDefs, featureFlags }) => {
  const chatbot = data.chatbot || {};
  const hasChatbotUsage = (Number(chatbot.queries) || 0) > 0;
  const storage = data.storage || {};

  return (
    <Box>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <SmartToy sx={{ color: '#4F46E5' }} />
          <Typography variant="subtitle1" fontWeight={600}>AI Chatbot usage (this month)</Typography>
        </Box>
        {!hasChatbotUsage ? (
          <EmptyState message="No chatbot usage recorded this month." height={100} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <KpiCard label="Queries this month" value={Number(chatbot.queries).toLocaleString('en-IN')} icon={<SmartToy sx={{ fontSize: 20 }} />} color="#4F46E5" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard label="Tokens used" value={Number(chatbot.tokens).toLocaleString('en-IN')} icon={<SmartToy sx={{ fontSize: 20 }} />} color="#6366f1" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard label="Est. cost" value={inr(chatbot.estimatedCostInr)} icon={<SmartToy sx={{ fontSize: 20 }} />} color="#0ea5e9" />
            </Grid>
          </Grid>
        )}
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <StorageIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="subtitle1" fontWeight={600}>Storage</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Database" value={formatBytes(storage.dbBytes)} icon={<StorageIcon sx={{ fontSize: 20 }} />} color="#4facfe" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Files" value={formatBytes(storage.fileBytes)} icon={<StorageIcon sx={{ fontSize: 20 }} />} color="#fa709a" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Total" value={formatBytes(storage.totalBytes)} icon={<StorageIcon sx={{ fontSize: 20 }} />} color="#f59e0b" />
          </Grid>
        </Grid>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Feature flags for this school</Typography>
        {featureDefs.length === 0 ? (
          <EmptyState message="Loading feature flags..." height={80} />
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {featureDefs.map((f) => {
              const enabled = featureFlags[f.key] !== false;
              return (
                <Box
                  key={f.key}
                  onClick={() => navigate('/superadmin/features')}
                  sx={{
                    px: 1.5, py: 0.75, borderRadius: 5, fontSize: '0.8rem', cursor: 'pointer',
                    bgcolor: enabled ? '#10b98115' : '#9ca3af15',
                    color: enabled ? '#10b981' : '#6b7280',
                    border: '1px solid', borderColor: enabled ? '#10b98140' : '#9ca3af40',
                  }}
                >
                  {f.label}
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Box>
  );
};

// =====================================================================
// Main page
// =====================================================================

const Analytics = () => {
  const navigate = useNavigate();
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [allSchools, setAllSchools] = useState([]);
  const [platformData, setPlatformData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [featureDefs, setFeatureDefs] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [schoolLoading, setSchoolLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        setLoading(true);
        const [schoolsRes, platformRes] = await Promise.all([
          superAdminAPI.getSchools(),
          superAdminAPI.getPlatformAnalytics(),
        ]);
        if (!alive) return;
        setAllSchools(schoolsRes.data.schools ?? []);
        setPlatformData(platformRes.data.data);
      } catch (error) {
        if (alive) {
          toast.error('Failed to load analytics');
          console.error(error);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!selectedSchoolId) {
      setSchoolData(null);
      setActiveTab(0);
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        setSchoolLoading(true);
        const [schoolRes, flagsRes] = await Promise.all([
          superAdminAPI.getSchoolAnalytics(selectedSchoolId),
          superAdminAPI.getFeatureFlags().catch(() => null),
        ]);
        if (!alive) return;
        setSchoolData(schoolRes.data.data);
        if (flagsRes) {
          setFeatureDefs(flagsRes.data.features || []);
          const entry = flagsRes.data.schools.find((s) => s.schoolId === selectedSchoolId);
          setFeatureFlags(entry?.flags || {});
        }
        setActiveTab(0);
      } catch (error) {
        if (alive) {
          toast.error('Failed to load school analytics');
          console.error(error);
        }
      } finally {
        if (alive) setSchoolLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [selectedSchoolId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const selectedSchool = allSchools.find((s) => s.id === selectedSchoolId) ?? null;
  const isPlatformMode = !selectedSchoolId;
  const tabs = isPlatformMode ? PLATFORM_TABS : SCHOOL_TABS;

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Analytics</Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              {selectedSchool ? `Viewing: ${selectedSchool.name}` : 'Platform-wide view across all schools'}
            </Typography>
            {selectedSchool && (
              <Button size="small" onClick={() => navigate(`/superadmin/schools/${selectedSchool.id}`)} sx={{ fontSize: '0.75rem', color: '#4F46E5' }}>
                View School Detail →
              </Button>
            )}
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Scope</InputLabel>
          <Select
            value={selectedSchoolId}
            label="Scope"
            onChange={(e) => setSelectedSchoolId(e.target.value)}
          >
            <MenuItem value="">
              <Box display="flex" alignItems="center" gap={1}>
                <PublicIcon sx={{ fontSize: 16, color: '#4F46E5' }} />
                All Schools (Platform)
              </Box>
            </MenuItem>
            <Divider />
            {allSchools.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.status === 'Active' ? '#10b981' : '#9ca3af' }} />
                  {s.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {tabs.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {schoolLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      ) : isPlatformMode ? (
        !platformData ? (
          <EmptyState message="No analytics data available" height={300} />
        ) : (
          <>
            {activeTab === 0 && <PlatformOverview data={platformData} allSchools={allSchools} navigate={navigate} />}
            {activeTab === 1 && <PlatformGrowth data={platformData} />}
            {activeTab === 2 && <PlatformFinance data={platformData} />}
            {activeTab === 3 && <PlatformEngagement data={platformData} />}
          </>
        )
      ) : !schoolData ? (
        <EmptyState message="Select a school to view its analytics" height={300} />
      ) : (
        <>
          {activeTab === 0 && <SchoolOverview data={schoolData} />}
          {activeTab === 1 && <SchoolFinance data={schoolData} />}
          {activeTab === 2 && <SchoolAttendance data={schoolData} />}
          {activeTab === 3 && (
            <SchoolUsage data={schoolData} navigate={navigate} featureDefs={featureDefs} featureFlags={featureFlags} />
          )}
        </>
      )}
    </Container>
  );
};

export default Analytics;
